import { useEffect, useState } from 'react';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-sm' : 'bg-transparent'}`}>
        <div className="max-w-[1400px] mx-auto px-20 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="https://public.readdy.ai/ai/img_res/11a07c52-f0ff-4ec4-9659-9719dc9b160d.png" alt="VIGA SH.P.K. Logo" className="h-10 w-10 object-contain" />
            <span className="text-base font-medium text-gray-900">VIGA SH.P.K.</span>
          </div>
          <div className="flex items-center gap-10">
            <a href="#home" className="text-sm text-gray-700 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">Home</a>
            <a href="#about" className="text-sm text-gray-700 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">About</a>
            <a href="#products" className="text-sm text-gray-700 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">Products</a>
            <a href="#features" className="text-sm text-gray-700 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">Features</a>
            <a href="#contact" className="text-sm text-gray-700 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">Contact</a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative pt-32 pb-24 px-20 min-h-screen flex items-center">
        <div className="max-w-[1400px] mx-auto w-full">
          <div className="flex items-center justify-between gap-16">
            <div className="flex-1">
              <h1 className="text-7xl font-light text-gray-900 leading-tight mb-8">
                iOS app development<br />
                <span className="font-normal">company</span><br />
                <span className="text-6xl text-gray-700">showcasing VPN and Cleaner apps</span>
              </h1>
              <p className="text-lg text-gray-600 max-w-xl leading-relaxed">
                A team of experts developing modern applications for the iOS platform. Our products are designed to enhance user experience and improve the security of mobile devices.
              </p>
            </div>
            <div className="w-[380px] h-[420px] rounded-[32px] bg-gradient-to-br from-teal-400 via-emerald-500 to-green-600 shadow-2xl flex items-center justify-center">
              <div className="text-white text-center">
                <i className="ri-smartphone-line text-8xl mb-4"></i>
                <p className="text-xl font-medium">iOS Excellence</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 px-20 bg-gray-50">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex items-start justify-between gap-20">
            <div className="flex-1">
              <p className="text-xs uppercase tracking-wider text-gray-500 mb-6">ABOUT VIGA SH.P.K.</p>
              <p className="text-2xl text-gray-800 leading-relaxed max-w-2xl">
                VIGA SH.P.K. is a team of experts developing modern applications for the iOS platform. We specialize in creating innovative solutions that enhance user experience and improve mobile device security. Our commitment to excellence drives us to deliver high-quality applications that meet the evolving needs of modern users.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-16">
              <div>
                <div className="text-6xl font-bold text-gray-900 mb-3">24/7</div>
                <div className="text-sm text-gray-600">Technical Support</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-32 px-20">
        <div className="max-w-[1400px] mx-auto">
          <div className="mb-16">
            <p className="text-xs uppercase tracking-wider text-gray-500 mb-4">OUR PRODUCTS</p>
            <h2 className="text-5xl font-bold text-gray-900">
              Enhancing user experience<br />
              <span className="bg-gradient-to-r from-teal-500 to-emerald-600 bg-clip-text text-transparent">and mobile security</span>
            </h2>
          </div>

          <div className="grid grid-cols-2 gap-12">
            <div className="bg-white border border-gray-200 rounded-3xl p-14 hover:shadow-xl transition-shadow duration-300">
              <div className="mb-8">
                <h3 className="text-5xl font-bold text-gray-900 mb-4">VPN</h3>
                <div className="w-full aspect-square bg-gradient-to-br from-teal-100 to-emerald-100 rounded-2xl flex items-center justify-center mb-8">
                  <i className="ri-shield-check-line text-9xl text-teal-600"></i>
                </div>
                <p className="text-base text-gray-600 leading-relaxed">
                  Secure connection<br />
                  Privacy protection
                </p>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-3xl p-14 hover:shadow-xl transition-shadow duration-300 mt-10">
              <div className="mb-8">
                <h3 className="text-5xl font-bold text-gray-900 mb-4">Cleaner</h3>
                <div className="w-full aspect-square bg-gradient-to-br from-emerald-100 to-green-100 rounded-2xl flex items-center justify-center mb-8">
                  <i className="ri-delete-bin-line text-9xl text-emerald-600"></i>
                </div>
                <p className="text-base text-gray-600 leading-relaxed">
                  Device optimization<br />
                  Storage management
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Company Section */}
      <section id="features" className="py-32 px-20 bg-gray-50">
        <div className="max-w-[1400px] mx-auto">
          <div className="mb-20">
            <p className="text-xs uppercase tracking-wider text-gray-500 mb-4">WHY CHOOSE US</p>
            <h2 className="text-5xl font-bold text-gray-900">
              Building trust through<br />
              <span className="bg-gradient-to-r from-teal-500 to-emerald-600 bg-clip-text text-transparent">innovation and quality</span>
            </h2>
          </div>

          <div className="grid grid-cols-3 gap-10">
            <div className="bg-white rounded-2xl p-10 border border-gray-100 hover:shadow-lg transition-shadow duration-300">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-teal-100 to-emerald-100 flex items-center justify-center mb-8">
                <i className="ri-focus-3-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Our Mission</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                To create innovative iOS applications that empower users with enhanced security and optimized device performance. We strive to make technology accessible and beneficial for everyone.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-10 border border-gray-100 hover:shadow-lg transition-shadow duration-300">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-100 to-green-100 flex items-center justify-center mb-8">
                <i className="ri-eye-line text-3xl text-emerald-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Our Vision</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                To become a leading iOS development company recognized for delivering exceptional applications that set new standards in mobile security and device optimization worldwide.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-10 border border-gray-100 hover:shadow-lg transition-shadow duration-300">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-green-100 to-teal-100 flex items-center justify-center mb-8">
                <i className="ri-heart-line text-3xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Our Values</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                We believe in transparency, user privacy, and continuous improvement. Our commitment to excellence drives every line of code we write and every feature we develop.
              </p>
            </div>
          </div>

          <div className="mt-20 grid grid-cols-2 gap-10">
            <div className="bg-gradient-to-br from-teal-600 to-emerald-700 rounded-3xl p-12">
              <h3 className="text-2xl font-bold text-white mb-6">Expert Development Team</h3>
              <p className="text-base text-white/90 leading-relaxed mb-8">
                Our team consists of skilled iOS developers, UI/UX designers, and security specialists who work together to create seamless and secure mobile experiences.
              </p>
              <div className="flex items-center gap-8">
                <div>
                  <div className="text-4xl font-bold text-white">iOS</div>
                  <div className="text-sm text-white/70">Platform Focus</div>
                </div>
                <div className="w-px h-12 bg-white/20"></div>
                <div>
                  <div className="text-4xl font-bold text-white">Swift</div>
                  <div className="text-sm text-white/70">Primary Language</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-12 border border-gray-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Commitment to Quality</h3>
              <p className="text-base text-gray-600 leading-relaxed mb-8">
                Every application we develop undergoes rigorous testing and quality assurance processes to ensure optimal performance, security, and user satisfaction.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                    <i className="ri-check-line text-lg text-teal-600"></i>
                  </div>
                  <span className="text-sm text-gray-700">Comprehensive Security Testing</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                    <i className="ri-check-line text-lg text-emerald-600"></i>
                  </div>
                  <span className="text-sm text-gray-700">Performance Optimization</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <i className="ri-check-line text-lg text-green-600"></i>
                  </div>
                  <span className="text-sm text-gray-700">Regular Updates & Maintenance</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 px-20 bg-gradient-to-br from-teal-600 via-emerald-600 to-green-700 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }}></div>
        </div>
        <div className="max-w-[1400px] mx-auto relative z-10">
          <div className="flex items-end justify-between">
            <div>
              <h2 className="text-6xl font-light text-white leading-tight mb-8">
                Ready to enhance<br />
                your mobile experience?
              </h2>
              <div className="inline-flex items-center gap-3 px-8 py-4 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
                <span className="text-white text-lg">Explore Our Apps</span>
                <i className="ri-arrow-right-line text-white text-xl"></i>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xl text-white/90 leading-relaxed max-w-md">
                Discover our innovative iOS applications<br />
                designed for modern users
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="bg-black mx-10 mb-0 rounded-t-[32px] pt-20 pb-12">
        <div className="max-w-[1400px] mx-auto px-20">
          <div className="grid grid-cols-3 gap-16 pb-16 border-b border-gray-800">
            <div>
              <div className="flex items-center gap-3 mb-8">
                <img src="https://public.readdy.ai/ai/img_res/11a07c52-f0ff-4ec4-9659-9719dc9b160d.png" alt="VIGA SH.P.K. Logo" className="h-8 w-8 object-contain" />
                <h3 className="text-2xl font-bold text-white">VIGA SH.P.K.</h3>
              </div>
              <p className="text-base text-gray-400 mb-12">
                Developing modern iOS applications
              </p>
              <div>
                <p className="text-sm text-white mb-4">Stay Updated</p>
                <div className="border-b border-gray-700 pb-2 mb-5">
                  <input 
                    type="email" 
                    placeholder="Enter your email" 
                    className="bg-transparent text-white placeholder-gray-500 outline-none w-full text-sm"
                  />
                </div>
                <div className="inline-flex items-center gap-2 px-6 py-3 bg-white rounded-full cursor-pointer hover:bg-gray-100 transition-colors whitespace-nowrap">
                  <span className="text-black text-sm font-medium">Subscribe</span>
                  <i className="ri-arrow-right-line text-black"></i>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-sm uppercase tracking-wider text-white mb-6">Quick Links</h4>
              <div className="space-y-4">
                <a href="#products" className="block text-sm text-gray-300 hover:text-white transition-colors cursor-pointer">VPN Application</a>
                <a href="#products" className="block text-sm text-gray-300 hover:text-white transition-colors cursor-pointer">Cleaner Application</a>
                <a href="#about" className="block text-sm text-gray-300 hover:text-white transition-colors cursor-pointer">About Us</a>
                <a href="#contact" className="block text-sm text-gray-300 hover:text-white transition-colors cursor-pointer">Support</a>
              </div>
            </div>

            <div>
              <h4 className="text-sm uppercase tracking-wider text-white mb-6">Contact Information</h4>
              <div className="space-y-6">
                <div>
                  <p className="text-xs text-gray-500 mb-2">Email</p>
                  <p className="text-sm text-white">info@viga-shpk.com</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-2">Number</p>
                  <p className="text-sm text-white">+1 9064225046</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-2">Address</p>
                  <p className="text-sm text-white leading-relaxed">
                    Lagjja Uznove, Rr. Antipatrea<br />
                    zk.8504, np. 197/11<br />
                    City BERAT<br />
                    Zip 5000<br />
                    Country Albania
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="pt-8 flex items-center justify-between">
            <div className="flex items-center gap-6">
              <span className="text-sm text-gray-500 hover:text-gray-300 transition-colors cursor-pointer">LinkedIn</span>
              <span className="text-sm text-gray-500 hover:text-gray-300 transition-colors cursor-pointer">Twitter</span>
              <span className="text-sm text-gray-500 hover:text-gray-300 transition-colors cursor-pointer">GitHub</span>
            </div>
            <div className="text-sm text-gray-600">
              © 2025 VIGA SH.P.K. All rights reserved
            </div>
            <div className="flex items-center gap-6">
              <span className="text-sm text-gray-500 hover:text-gray-300 transition-colors cursor-pointer">Privacy Policy</span>
              <span className="text-sm text-gray-500 hover:text-gray-300 transition-colors cursor-pointer">Terms of Service</span>
            </div>
          </div>

          <div className="pt-6 text-center">
            <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-xs text-gray-600 hover:text-gray-400 transition-colors">
              Powered by Readdy
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
